export interface LocalStorageItem<Value> {
    key: string
    get: () => Value
    set: (value: Value) => void
    remove: () => void
    subscribe: (listener: () => void) => () => void
}

interface LocalStorageItemOptions<Value> {
    key: string
    fallback: Value
    parse: (raw: string) => Value | null
    serialize?: (value: Value) => string
}

const listenersByKey = new Map<string, Set<() => void>>()

const notify = (key: string): void => {
    listenersByKey.get(key)?.forEach(listener => listener())
}

const handleStorageEvent = (event: StorageEvent): void => {
    if (event.storageArea !== localStorage) {
        return
    }

    if (event.key === null) {
        listenersByKey.forEach((_, key) => notify(key))
    } else {
        notify(event.key)
    }
}

export const subscribeToLocalStorageKey = (key: string, listener: () => void): (() => void) => {
    if (listenersByKey.size === 0 && typeof window !== 'undefined') {
        window.addEventListener('storage', handleStorageEvent)
    }

    const listeners = listenersByKey.get(key) ?? new Set()

    listeners.add(listener)
    listenersByKey.set(key, listeners)

    return () => {
        listeners.delete(listener)

        if (listeners.size === 0) {
            listenersByKey.delete(key)
        }

        if (listenersByKey.size === 0 && typeof window !== 'undefined') {
            window.removeEventListener('storage', handleStorageEvent)
        }
    }
}

const readRaw = (key: string): string | null => {
    try {
        return localStorage.getItem(key)
    } catch {
        return null
    }
}

export const createLocalStorageItem = <Value>({
    key,
    fallback,
    parse,
    serialize = String,
}: LocalStorageItemOptions<Value>): LocalStorageItem<Value> => {
    // Cached per item: a component that recreates an item on every render should only use it for primitive values
    let snapshot: { raw: string, value: Value } | undefined

    const get = (): Value => {
        const raw = readRaw(key)

        if (raw === null) {
            return fallback
        }

        // Parsing once per stored value keeps the result referentially stable for useSyncExternalStore
        if (snapshot && snapshot.raw === raw) {
            return snapshot.value
        }

        let value: Value | null

        try {
            value = parse(raw)
        } catch {
            value = null
        }

        const resolvedValue = value ?? fallback

        snapshot = { raw, value: resolvedValue }

        return resolvedValue
    }

    const set = (value: Value): void => {
        try {
            localStorage.setItem(key, serialize(value))
        } catch {
            // localStorage may be unavailable; persisting is best-effort only.
        }

        notify(key)
    }

    const remove = (): void => {
        try {
            localStorage.removeItem(key)
        } catch {
            // localStorage may be unavailable; persisting is best-effort only.
        }

        notify(key)
    }

    return {
        key,
        get,
        set,
        remove,
        subscribe: listener => subscribeToLocalStorageKey(key, listener),
    }
}

export const parseString = (raw: string): string => raw

export const parseBoolean = (raw: string): boolean | null => {
    if (raw === 'true') {
        return true
    }

    return raw === 'false' ? false : null
}

export const parseDate = (raw: string): Date | null => {
    const date = new Date(raw)

    return Number.isNaN(date.getTime()) ? null : date
}

export const serializeDate = (date: Date): string => date.toISOString()
